import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout/Layout.jsx";
import DashboardLayout from "./components/DashboardLayout.jsx";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import TollIcon from "@mui/icons-material/Toll";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import "./css/Dashboard.css";
import { apiClient } from "../config/api.client.js";
import { message } from "antd";

const Dashboard = () => {
	const { user } = useSelector((state) => state.user);
	const navigate = useNavigate();
	const params = useParams();
	const [dashboardData, setDashboardData] = useState(null);
	const [loading, setLoading] = useState(false);

	// Fetch dashboard data from API
	const getDashboardData = async () => {
		try {
			setLoading(true);
			const res = await apiClient.get("/user/dashboard");
			
			if (res.data && res.data.data) {
				setDashboardData(res.data.data);
			} else {
				message.error("Failed to fetch dashboard data");
			}
		} catch (error) {
			console.error("Error fetching dashboard data:", error);
			message.error(error.response?.data?.message || "Failed to fetch dashboard data");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (user !== null) {
			getDashboardData();
		}
	}, [user]);

	// Show loading state
	if (loading) {
		return (
			<Layout>
				<DashboardLayout>
					<div className="dashboard-wrapper">
						<div className="dashboard-loading">
							<div className="loading-hero">
								<div className="loading-hero-content">
									<div className="loading-skeleton-title"></div>
									<div className="loading-skeleton-subtitle"></div>
								</div>
								<div className="loading-hero-stats">
									<div className="loading-skeleton-stat"></div>
									<div className="loading-skeleton-stat"></div>
								</div>
							</div>
							<div className="loading-cards">
								{[1, 2, 3, 4, 5].map((index) => (
									<div key={index} className="loading-card">
										<div className="loading-card-header">
											<div className="loading-skeleton-icon"></div>
											<div className="loading-skeleton-badge"></div>
										</div>
										<div className="loading-card-content">
											<div className="loading-skeleton-value"></div>
											<div className="loading-skeleton-title"></div>
											<div className="loading-skeleton-subtitle"></div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</DashboardLayout>
			</Layout>
		);
	}

	return (
<>
<div style={{backgroundColor: "black"}}>

		<Layout>
			<DashboardLayout>
				<div className="dashboard-wrapper">
					{/* Hero Section */}
					<div className="dashboard-hero">
						<div className="hero-content">
							<h1 className="hero-title">Welcome back, {user?.name || 'User'}!</h1>
							<p className="hero-subtitle">Manage your gaming transactions and track your progress</p>
						</div>
						<div className="hero-stats">
							<div className="quick-stat">
								<span className="stat-value">₹{parseFloat(dashboardData?.walletBalance || 0).toFixed(2)}</span>
								<span className="stat-label">Wallet Balance</span>
							</div>
							<div className="quick-stat">
								<span className="stat-value">{dashboardData?.orders?.total || 0}</span>
								<span className="stat-label">Total Orders</span>
							</div>
						</div>
					</div>

					{/* Dashboard Cards Grid */}
					<div className="user-dashboard">
						{/* Wallet Balance Card */}
						<div
							className="dash-card wallet-card"
							onClick={() => navigate("/wallet")}
						>
							<div className="card-header">
								<TollIcon className="card-icon" />
								<div className="card-badge">Primary</div>
							</div>
							<div className="card-content">
								<h2 className="card-value">
									₹{parseFloat(dashboardData?.walletBalance || 0).toFixed(2)}
								</h2>
								<p className="card-title">Wallet Balance</p>
								<small className="card-subtitle">Available for transactions</small>
							</div>
						</div>

						{/* Orders Card */}
						<div
							className="dash-card orders-card"
							onClick={() => navigate("/orders")}
						>
							<div className="card-header">
								<InstallMobileIcon className="card-icon" />
								<div className="card-progress">
									<div className="progress-bar">
										<div 
											className="progress-fill" 
											style={{
												width: `${dashboardData?.orders?.total > 0 ? 
													(dashboardData?.orders?.completedCount / dashboardData?.orders?.total) * 100 : 0}%`
											}}
										></div>
									</div>
								</div>
							</div>
							<div className="card-content">
								<h2 className="card-value">{dashboardData?.orders?.total || 0}</h2>
								<p className="card-title">Total Orders</p>
								<small className="card-subtitle">
									{dashboardData?.orders?.completedCount || 0} completed
								</small>
							</div>
						</div>

						{/* Transactions Card */}
						<div
							className="dash-card transactions-card"
							onClick={() => navigate("/payments")}
						>
							<div className="card-header">
								<PointOfSaleOutlinedIcon className="card-icon" />
								<div className="card-trend">
									<span className="trend-indicator up">↗</span>
								</div>
							</div>
							<div className="card-content">
								<h2 className="card-value">{dashboardData?.transactions?.total || 0}</h2>
								<p className="card-title">Transactions</p>
								<small className="card-subtitle">
									₹{parseFloat(dashboardData?.transactions?.successAmount || 0).toFixed(2)} successful
								</small>
							</div>
						</div>

						{/* Order Success Amount Card */}
						<div
							className="dash-card success-card"
							onClick={() => navigate("/orders")}
						>
							<div className="card-header">
								<PointOfSaleOutlinedIcon className="card-icon" />
								<div className="card-badge success">Success</div>
							</div>
							<div className="card-content">
								<h2 className="card-value">
									₹{parseFloat(dashboardData?.orders?.successAmount || 0).toFixed(2)}
								</h2>
								<p className="card-title">Order Success Amount</p>
								<small className="card-subtitle">Total successful transactions</small>
							</div>
						</div>

						{/* My Account Card */}
						<div
							className="dash-card account-card"
							onClick={() => navigate("/my-account")}
						>
							<div className="card-header">
								<AccountCircleOutlinedIcon className="card-icon" />
							</div>
							<div className="card-content">
								<p className="card-title large">My Account</p>
								<small className="card-subtitle">Manage your profile and settings</small>
							</div>
						</div>
					</div>
				</div>
			</DashboardLayout>
		</Layout>
</div>

</>
	);
};

export default Dashboard;